import isFunction from 'lodash/isFunction';
import some from 'lodash/some';
import values from 'lodash/values';
import type { LocalIssueId } from '@atlassian/jira-polaris-domain-idea/src/idea/types.tsx';
import type { Filter, TextFilter } from '@atlassian/jira-polaris-domain-view/src/filter/types.tsx';
import type { Props, State } from '../types.tsx';
import type { FieldMapping } from '../utils/field-mapping/types.tsx';

export type IssueFilterFunction = (
	state: State,
	props: Props | undefined,
	localIssueId: LocalIssueId,
) => boolean;

export const getResolvedFilters = (
	filters: Filter[],
	fieldMappings: FieldMapping<unknown>[],
	getQuickSearchFilterFunc?: (
		fieldMapping: FieldMapping<unknown>,
		filter: TextFilter,
	) => IssueFilterFunction,
) => {
	const conjunctFilters: Array<IssueFilterFunction> = [];
	const disjunctFilters: Record<string, Array<IssueFilterFunction>> = {};
	const quickFilters: Record<string, Array<IssueFilterFunction>> = {};

	filters.forEach((filter) => {
		fieldMappings.forEach((mapping: FieldMapping<unknown>) => {
			const filterValueFunc = mapping.getFilter(filter);
			if (filterValueFunc !== undefined) {
				if (isFunction(filterValueFunc)) {
					const filterFunc: IssueFilterFunction = (state, props, id) => {
						const value = mapping.valueAccessor(state, props, id);
						return filterValueFunc(value, state, props, id);
					};
					conjunctFilters.push(filterFunc);
				} else if (filterValueFunc.disjunctionIdentifier !== undefined) {
					const filterFunc: IssueFilterFunction = (state, props, id) => {
						const value = mapping.valueAccessor(state, props, id);
						return filterValueFunc.filterFunction(value, state, props, id);
					};
					if (!('localId' in filter) || filter.localId !== 'quick') {
						disjunctFilters[filterValueFunc.disjunctionIdentifier] = [
							...(disjunctFilters[filterValueFunc.disjunctionIdentifier] || []),
							filterFunc,
						];
					} else if (getQuickSearchFilterFunc) {
						quickFilters[filterValueFunc.disjunctionIdentifier] = [
							...(quickFilters[filterValueFunc.disjunctionIdentifier] || []),
							getQuickSearchFilterFunc(mapping, filter),
						];
					}
				}
			}
		});
	});

	const resolvedQuickFilters = values(quickFilters).map(
		(filtersGroup): IssueFilterFunction =>
			(state, props, id) =>
				some(filtersGroup, (func) => func(state, props, id)),
	);

	const resolvedDisjunctFilters = values(disjunctFilters).map(
		(disjunctionGroup): IssueFilterFunction =>
			(state, props, id) =>
				some(disjunctionGroup, (func) => func(state, props, id)),
	);

	return [...conjunctFilters, ...resolvedDisjunctFilters, ...resolvedQuickFilters];
};
